// src/GameStatsTables.js
import React from "react";
import { calculateTotals } from "./GameStatsTableHelper";

export default function GameStatsTables({ stats = [], games = [], teamId, showEditButtons = false, onEdit }) {
  if (!stats.length) return <p>No stats available.</p>;
  if (!games.length) return <p>No games available.</p>;

  const statFields = [
    { key: "two_fg_attempts", label: "2FGA" },
    { key: "two_fg_made", label: "2FGM" },
    { key: "three_fg_attempts", label: "3FGA" },
    { key: "three_fg_made", label: "3FGM" },
    { key: "three_pt_percentage", label: "3P%" },
    { key: "ft_attempts", label: "FTA" },
    { key: "ft_made", label: "FTM" },
    { key: "ft_percentage", label: "FT%" },
    { key: "total_points", label: "PTS" },
    { key: "fg_attempts", label: "FGA" },
    { key: "fg_made", label: "FGM" },
    { key: "fg_percentage", label: "FG%" },
    { key: "off_rebounds", label: "OREB" },
    { key: "def_rebounds", label: "DREB" },
    { key: "treb", label: "TREB" },
    { key: "assists", label: "AST" },
    { key: "steals", label: "STL" },
    { key: "blocks", label: "BL" },
    { key: "turnovers", label: "TO" },
    { key: "fouls", label: "FOUL" },
    { key: "minutes_formatted", label: "MIN" },
    { key: "bpm", label: "PM" },
  ];


  // Group stats by game_id
  const statsByGame = stats.reduce((acc, s) => {
    if (!acc[s.game_id]) acc[s.game_id] = [];
    acc[s.game_id].push(s);
    return acc;
  }, {});

  // Calculate team totals for each game
  const teamTotalsByGame = Object.fromEntries(
    Object.entries(statsByGame).map(([gameId, gameStats]) => {
      const totals = gameStats.reduce((acc, s) => {
        acc.two_fg_made      += Number(s.two_fg_made)      || 0;
        acc.two_fg_attempts  += Number(s.two_fg_attempts)  || 0;
        acc.three_fg_made    += Number(s.three_fg_made)    || 0;
        acc.three_fg_attempts+= Number(s.three_fg_attempts)|| 0;
        acc.ft_made          += Number(s.ft_made)          || 0;
        acc.ft_attempts      += Number(s.ft_attempts)      || 0;
        acc.off_rebounds     += Number(s.off_rebounds)     || 0;
        acc.def_rebounds     += Number(s.def_rebounds)     || 0;
        acc.assists          += Number(s.assists)          || 0;
        acc.steals           += Number(s.steals)           || 0;
        acc.blocks           += Number(s.blocks)           || 0;
        acc.turnovers        += Number(s.turnovers)        || 0;
        acc.fouls            += Number(s.fouls)            || 0;
        acc.minutes          += Number(s.minutes)          || 0;

        return acc;
      }, {
        two_fg_made: 0,
        two_fg_attempts: 0,
        three_fg_made: 0,
        three_fg_attempts: 0,
        ft_made: 0,
        ft_attempts: 0,
        off_rebounds: 0,
        def_rebounds: 0,
        assists: 0,
        steals: 0,
        blocks: 0,
        turnovers: 0,
        fouls: 0,
        minutes: 0
      });
      return [gameId, totals];
    })
  );


  return (
    <div className="space-y-8">
      {Object.entries(statsByGame)
        .sort((a, b) => new Date(games.find(g => g.id === Number(b[0]))?.date) - new Date(games.find(g => g.id === Number(a[0]))?.date))
        .map(([gameId, gameStats]) => {
          const game = games.find(g => g.id === Number(gameId));
          if (!game) return null;

          const defaultTeamIdNum = Number(teamId);
          const isHome = Number(game.home_team_id) === defaultTeamIdNum;
          const isAway = Number(game.away_team_id) === defaultTeamIdNum;
          if (!isHome && !isAway) return null;

          const opponent = isHome ? game.away_team : game.home_team;

          // --- Compute team totals per game ---
          const teamStats = gameStats.filter(s => Number(s.team_id) === defaultTeamIdNum);
          const teamTotals = calculateTotals(teamStats, teamTotalsByGame[gameId]);

          return (
            <div key={gameId}>
              <h4 className="font-bold mb-2">
                {opponent} — {game.date} {isHome ? "(Home)" : "(Away)"}
              </h4>
              <table className="table-auto border-collapse border border-gray-300 w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-2 py-1 text-left">Player</th>
                    {statFields.map(f => <th key={f.key} className="border px-2 py-1">{f.label}</th>)}
                    {showEditButtons && <th className="border px-2 py-1">Edit</th>}
                  </tr>
                </thead>
                <tbody>
                  {teamStats.map(s => {
                    const playerTotals = calculateTotals([s], teamTotalsByGame[gameId]);
                    return (
                      <tr key={s.id} className="hover:bg-gray-50">
                        <td className="border px-2 py-1">{s.player_name}</td>
                        {statFields.map(f => <td key={f.key} className="border px-2 py-1">{playerTotals[f.key]}</td>)}
                        {showEditButtons && (
                          <td className="border px-2 py-1">
                            <button onClick={() => onEdit(s)} className="bg-yellow-400 text-black px-3 py-1 rounded hover:bg-yellow-500">Edit</button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  <tr className="font-bold bg-gray-200">
                    <td className="border px-2 py-1">Team Total</td>
                    {statFields.map(f => <td key={f.key} className="border px-2 py-1">{teamTotals[f.key]}</td>)}
                    {showEditButtons && <td className="border px-2 py-1"></td>}
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}
    </div>
  );
}
